import React, { useState } from "react"

import useDimensions from "react-use-dimensions"
import "./styles.scss"
import DataLoader from "../DataLoader"
import { scaleLinear } from "d3-scale"
import { gradientColors } from "../utils/gradient"
import CountryFactorFillGraphTooltip from "./CountryFactorFillGraphTooltip"
import { useSpring, animated, useTransition } from "react-spring"
import useDetectPrint from "use-detect-print"
import { useSmallWidthMode } from "../utils/smallWidthMode"

const printFullWidth = 700
const printHalfWidth = printFullWidth - 245

export default function CountryFactorFillGraph(props) {
  const [tooltipData, setTooltipData] = useState(null)
  const { selectedCountry, selectedYear, scoreKey, countryData } = props

  const [ref, containerDimensions] = useDimensions()
  const margins = {
    top: 40,
    left: 30,
    right: 30,
    bottom: 10,
  }
  const containerHeight = 80
  const isPrinting = useDetectPrint()
  const inSmallWidthMode = useSmallWidthMode()

  let graphContents = null
  //let defs = null
  const [selectedValues, setSelectedValues] = useSpring(() => ({
    from: { x: 0, score: 0 },
    to: { x: 0, score: 0 },
  }))
  const countries = DataLoader.dataByYear[selectedYear].countries

  let containerWidth = containerDimensions.width || 400
  if (isPrinting) {
    if (props.printStyle === "fullWidth") {
      containerWidth = printFullWidth
    } else if (props.printStyle === "halfWidth") {
      containerWidth = printHalfWidth
    }
  }
  let width = containerWidth - margins.left - margins.right
  let height = containerHeight - margins.top - margins.bottom

  const xScale = scaleLinear().domain([0, 1]).range([0, width])

  const getTrianglePositions = (country) => {
    const score = country[scoreKey]
    const x = xScale(score ?? 0)
    const selected = country.Country === selectedCountry
    const hovered = tooltipData ? country === tooltipData.country : false
    // Blank 2015/2016 sub-factor scores are null: hide that country's marker
    const opacity =
      score === null
        ? 0
        : selected || hovered
        ? 1
        : inSmallWidthMode
        ? 0.05
        : 0.1
    const fill = hovered ? "#482D8B" : "#000000"

    if (selected) {
      setSelectedValues({ x, score: score ?? 0 })
    }
    return { x, opacity, fill }
  }
  const triangleTransitions = useTransition(
    containerDimensions.width ? countries : [],
    (d) => d.Country,
    {
      enter: getTrianglePositions,
      update: getTrianglePositions,
    },
  )

  const groups = [
    {
      attribute: "Region",
      label: inSmallWidthMode ? "Reg. Avg" : "Regional Avg",
      value: DataLoader.dataByYear[selectedYear].groupAverages.Region.find(
        (groupValue) => groupValue.group === countryData.Region,
      )[scoreKey],
    },
    {
      attribute: "Global",
      label: "Global Avg",
      value: DataLoader.dataByYear[selectedYear].groupAverages.global[scoreKey],
    },
  ].sort((a, b) => a.value - b.value)

  const getGroupPositions = (group) => {
    const value = group.value
    const x = xScale(value)
    return { x, value }
  }
  const groupTransitions = useTransition(
    containerDimensions.width ? groups : [],
    (d) => d.attribute,
    {
      enter: getGroupPositions,
      update: getGroupPositions,
    },
  )

  const hoverCountry = (country, _x, _y) => {
    return (event) => {
      if (inSmallWidthMode) {
        return setTooltipData(null)
      }
      if (!country) {
        return setTooltipData(null)
      }

      let x = event.pageX
      const targetPosition = event.target.getBoundingClientRect()
      let y = targetPosition.y - 1
      setTooltipData({ country, x, y })
    }
  }

  const barHeight = 20
  const miniBarHeight = barHeight * 0.2
  const triangles = triangleTransitions.map((transition, countryIndex) => {
    if (transition.state === "leave") {
      return null
    }
    const country = transition.item
    const { x, fill, opacity } = transition.props
    return (
      <animated.g
        key={country.Country}
        transform={x.interpolate((x) => `translate(${x}, 0)`)}
        y={margins.top}
        onMouseOver={hoverCountry(country, x, 0)}
        onMouseMove={hoverCountry(country, x, 0)}
        onMouseOut={hoverCountry(null)}
      >
        <animated.polygon
          points={"0,0 -5,-10 5,-10"}
          fill={fill}
          opacity={opacity}
        />
      </animated.g>
    )
  })

  const groupLines = groupTransitions.map((item, groupIndex) => {
    const group = item.item
    const { x, value } = item.props
    const textX = groupIndex === 0 ? -5 : 5
    const textAnchor = groupIndex === 0 ? "end" : "start"
    return (
      <animated.g
        key={groupIndex}
        transform={x.interpolate((n) => `translate(${n}, 0)`)}
      >
        <line y2={height + 8} stroke="#707070" strokeWidth={3} />
        <animated.text
          className="groupLabel"
          y={height + 8}
          x={textX}
          textAnchor={textAnchor}
        >
          {value.interpolate(
            (v) => `${group.label.toUpperCase()} ${v.toFixed(2)}`,
          )}
        </animated.text>
      </animated.g>
    )
  })

  const gradientId = `fillGraphGradient-${(
    (Math.random() * 0xffffff) <<
    0
  ).toString(16)}`
  graphContents = (
    <g transform={`translate(${margins.left + 0.5}, ${margins.top + 0.5})`}>
      {triangles}
      <rect
        width={width}
        height={barHeight}
        stroke="#707070"
        fill="#ebebeb"
        strokeWidth="0.5"
      />
      <rect width={width} height={miniBarHeight} fill={`url(#${gradientId})`} />
      <rect width={width} height={barHeight} fill={`url(#${gradientId})`} />
      <animated.rect
        x={selectedValues.x}
        width={selectedValues.x.interpolate((x) => Math.max(0, width - x))}
        height={barHeight - miniBarHeight}
        y={miniBarHeight}
        fill="#ebebeb"
      />
      <animated.line
        x1={selectedValues.x}
        x2={selectedValues.x}
        strokeWidth={3}
        y1={barHeight}
        stroke="black"
      />
      <text x={-5} y={height / 2} textAnchor="end" className="graphAxisLabel">
        0.0
      </text>
      <text x={width + 5} y={height / 2} className="graphAxisLabel">
        1.0
      </text>
      <animated.text x={selectedValues.x} y={-15} textAnchor="middle">
        {countryData[scoreKey] === null
          ? `${selectedCountry.toUpperCase()} N/A`
          : selectedValues.score.interpolate(
              (n) => `${selectedCountry.toUpperCase()} ${n.toFixed(2)}`,
            )}
      </animated.text>
      {groupLines}
    </g>
  )

  return (
    <div ref={ref} className="countryFactorFillGraphWrapper">
      <svg
        viewBox={`0 0 ${containerWidth} ${containerHeight}`}
        width={containerWidth}
        height={containerHeight}
      >
        <defs>
          <linearGradient id={gradientId}>
            {gradientColors.map((color) => (
              <stop
                key={color.offset}
                offset={`${color.offset}%`}
                stopColor={color.hex}
              />
            ))}
          </linearGradient>
        </defs>
        {graphContents}
      </svg>
      {tooltipData ? (
        <CountryFactorFillGraphTooltip {...tooltipData} scoreKey={scoreKey} />
      ) : null}
    </div>
  )
}
